import React, { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import BedbankCancellationPolicyModal from './BedbankCancellationPolicyModal'
import { getPromotionCancellationPolicyByCode } from '@luxuryescapes/lib-refunds/build'
import Clickable from 'components/Common/Clickable'
import BodyText from 'LuxKit/Typography/BodyText'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import ModalContext from 'contexts/ModalContext'
import Group from 'components/utils/Group'

interface Props {
  policyCode: string
}

function BedbankPromotionCancellationPolicy(props: Props) {
  const { policyCode } = props
  const intl = useIntl()
  const showModal = useContext(ModalContext)

  const policyNumberOfDays = getPromotionCancellationPolicyByCode(policyCode)?.numberOfDays ?? 0

  const nonRefundableTitle = intl.formatMessage({
    id: 'eMXjwPm0Zo9CsSBLkiDs',
    defaultMessage: 'Non-refundable',
    description: 'Title for non-refundable bedbank promotion cancellation policy',
  })
  const freeCancellationTitle = intl.formatMessage({
    id: 'AjY7o7To2JaQnNlYVms5',
    defaultMessage: 'Free Cancellation',
    description: 'Title for free cancellation bedbank promotion policy',
  })
  const policyTitle = policyCode === 'non-refundable' ? nonRefundableTitle : freeCancellationTitle

  const policyDescription = useMemo(() => {
    if (policyCode === 'non-refundable') {
      return intl.formatMessage({
        id: 'GeEm2O_vrlkgcDh5DgYO',
        defaultMessage: 'If you cancel your booking, you are not eligible for a refund or credit for a future stay.',
        description: 'Description for non-refundable bedbank promotion cancellation policy',
      })
    } else {
      const daysText = intl.formatMessage(
        {
          id: 'fk8iKWxrqySUJMAH17ki',
          defaultMessage: '{count, plural, one {# day} other {# days}}',
          description: 'Pluralised day count for cancellation policy lead time',
        },
        { count: policyNumberOfDays },
      )
      return intl.formatMessage(
        {
          id: 'oCfuSyUgcTOfvqDDZxPI',
          defaultMessage: 'Accommodation bookings can be cancelled with a full refund provided the cancellation is made no less than {daysText} prior to check-in. That is cancel by 11:59 pm (local time of property), {daysText} prior to the day of check-in. Excludes flights.',
          description: 'Description for refundable bedbank promotion cancellation policy with day count',
        },
        { daysText },
      )
    }
  }, [policyNumberOfDays, policyCode, intl])

  const showPolicy = useCallback(() => {
    showModal(<BedbankCancellationPolicyModal heading={policyTitle} policyDescriptions={[policyDescription]} />)
  }, [policyTitle, policyDescription, showModal])

  return <Clickable onClick={showPolicy}>
    <Group direction="horizontal" gap={4} verticalAlign="center">
      <BodyText variant="medium" wrap="no-wrap" weight="bold" colour={policyCode !== 'non-refundable' ? 'highlight-secondary' : undefined}>{policyTitle}</BodyText>
      <LineInfoCircleIcon colour="success" size="XS" />
    </Group>
  </Clickable>
}

export default BedbankPromotionCancellationPolicy
